export type ReportDraftSaveQueueStatus = "saved" | "unsaved" | "saving" | "conflict" | "error";

export interface ReportDraftSaveQueueState {
  status: ReportDraftSaveQueueStatus;
  message?: string;
}

export type ReportDraftSaveOutcome =
  | { ok: true }
  | { ok: false; conflict: boolean; message: string };

interface QueueEntry<T> {
  latest?: T;
  running: boolean;
  blocked: false | "conflict" | "error";
}

/** Coalesces edits per report while guaranteeing one revision-aware write at a time. */
export class ReportDraftSaveQueue<T> {
  private readonly entries = new Map<string, QueueEntry<T>>();

  constructor(
    private readonly save: (reportId: string, value: T) => Promise<ReportDraftSaveOutcome>,
    private readonly onState: (reportId: string, state: ReportDraftSaveQueueState) => void,
  ) {}

  enqueue(reportId: string, value: T): void {
    const entry = this.entry(reportId);
    entry.latest = value;
    this.onState(reportId, { status: entry.blocked || "unsaved", message: entry.blocked ? "Pending edits were updated but remain blocked until you deliberately retry or discard them." : undefined });
    if (!entry.running && !entry.blocked) void this.drain(reportId, entry);
  }

  retry(reportId: string, rebase?: (value: T) => T): void {
    const entry = this.entries.get(reportId);
    if (!entry?.latest || entry.running) return;
    if (rebase) entry.latest = rebase(entry.latest);
    entry.blocked = false;
    this.onState(reportId, { status: "unsaved" });
    void this.drain(reportId, entry);
  }

  discard(reportId: string): void {
    const entry = this.entries.get(reportId);
    if (!entry || entry.running) return;
    entry.latest = undefined;
    entry.blocked = false;
    this.entries.delete(reportId);
    this.onState(reportId, { status: "saved" });
  }

  hasPending(reportId: string): boolean {
    return Boolean(this.entries.get(reportId)?.latest);
  }

  private entry(reportId: string): QueueEntry<T> {
    let entry = this.entries.get(reportId);
    if (!entry) {
      entry = { running: false, blocked: false };
      this.entries.set(reportId, entry);
    }
    return entry;
  }

  private async drain(reportId: string, entry: QueueEntry<T>): Promise<void> {
    if (entry.running || entry.blocked) return;
    entry.running = true;
    while (entry.latest && !entry.blocked) {
      const current = entry.latest;
      entry.latest = undefined;
      this.onState(reportId, { status: "saving" });
      let result: ReportDraftSaveOutcome;
      try {
        result = await this.save(reportId, current);
      } catch (error) {
        result = { ok: false, conflict: false, message: error instanceof Error ? error.message : "The hosted report draft could not be saved." };
      }
      if (!result.ok) {
        entry.latest ??= current;
        entry.blocked = result.conflict ? "conflict" : "error";
        this.onState(reportId, { status: entry.blocked, message: result.message });
        break;
      }
    }
    entry.running = false;
    if (!entry.blocked && !entry.latest) {
      this.entries.delete(reportId);
      this.onState(reportId, { status: "saved" });
    }
  }
}
